import { Checkbox, TextBox } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useEffect, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { arrowRotateCwIcon, saveIcon } from "@progress/kendo-svg-icons";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { loadingPanel } from "../../LoadingPanel";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { isBooleanOption } from "./booleanOptions";

export const ConfigLocatorCollector = () => {

    const localizationService = useLocalization();
    let languageId = localStorage.getItem('languageId');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    const [selected, setSelected] = useState<number>(0);

    const [tabList, setTabList] = useState<any>([]);
    const [newTabList, setNewTabList] = useState<any>([]);
    const [tabNameList, setTabNameList] = useState<any>([]);
    const [groupSearchValue, setGroupSearchValue] = useState<string>('');
    const [settingsSearchValue, setSettingsSearchValue] = useState<string>('');

    const [loading, setLoading] = useState(false);
    const [updated, setUpdated] = useState(false);

    useEffect(() => {
        getTabList();
    }, [])

    const getTabGroupName = (tab: any) => `${tab?.groupDescription ?? tab?.group ?? ''}`.trim();

    const getTabList = async () => {

        setLoading(true);

        let languageId = localStorage.getItem('languageId');

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                const res = await axios.get(`${SERVER_URL}/settings/Collector`, { headers })

                const data = res.data

                let tabList: any = [];
                let tabNameList: any = [];
                let tabIdList: any = [];
                let tabDescriptionList: any = [];
                let tabValueList: any = [];
                data.data.forEach((tab: any) => {
                    const tabGroupName = getTabGroupName(tab);

                    if (tabGroupName && !tabNameList.includes(tabGroupName)) {
                        tabNameList.push(tabGroupName)
                    }
                    tabList.push(tab)
                    tabIdList.push({ id: tab.id })
                    tabDescriptionList.push(tab.description)
                    tabValueList.push({ value: tab.value })
                })
                setTabList(tabList)
                setTabNameList(tabNameList)
                setNewTabList([])

                setLoading(false);

            } catch (error: any) {
                //setError(error);
                setLoading(false);
            }
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const handleChangeTextbox = (e: any, id: any, groupName: string) => {
        let currTabList: any = newTabList != '' ? newTabList : tabList;
        const value: any = e.target.value
        currTabList = currTabList.map((element: any) => {
            if (element.id == id && getTabGroupName(element) == groupName) {
                return { ...element, value: value }
            } else {
                return element
            }
        });
        setNewTabList(currTabList);
    };

    const handleChange = (e: any, id: any, groupName: string) => {
        let currTabList: any = newTabList != '' ? newTabList : tabList;
        const value: any = e.target.value == true ? "1" : "0";
        currTabList = currTabList.map((element: any) => {
            if (element.id == id && getTabGroupName(element) == groupName) {
                return { ...element, value: value }
            } else {
                return element
            }
        });
        setNewTabList(currTabList);
    };

    const getDefaultDeviceTypeOptionsByTabName = (tabName: string) => {
        if (tabName === 'LRA') {
            return [
                { value: "0", text: "UTAG_PBL" },
                { value: "1", text: "UTAGPBL_R" }
            ];
        }

        if (tabName === 'SKY') {
            return [
                { value: "99", text: "TEMPERATURE" }
            ];
        }

        if (tabName === 'TLK') {
            return [
                { value: "0", text: "NeoBox" },
                { value: "1", text: "UTag4" },
                { value: "2", text: "SAVL" },
                { value: "3", text: "UTagPBG" }
            ];
        }

        return [           
        ];
    };

    const networkServerOptions = [
        { value: "0", text: "A2A" }
    ];

    const handleChangeDropdown = (e: any, id: any, groupName: string) => {
        let currTabList: any = newTabList != '' ? newTabList : tabList;
        const value = e.target.value?.value ?? "0";
        currTabList = currTabList.map((element: any) => {
            if (element.id == id && getTabGroupName(element) == groupName) {
                return { ...element, value };
            } else {
                return element;
            }
        });
        setNewTabList(currTabList);
    };

    const saveGeneralsParams = async () => {

        // Scroll alla cima della pagina PRIMA di tutto
        const scrollToTop = () => {
            // Scroll della window
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
            // Scroll di tutti i possibili contenitori con classe o attributi comuni
            const scrollableElements = document.querySelectorAll('[class*="container"], [class*="content"], [class*="main"], .k-content, .k-tabstrip-content');
            scrollableElements.forEach((element: any) => {
                if (element.scrollTop !== undefined) {
                    element.scrollTop = 0;
                }
            });
        };

        scrollToTop();

        setLoading(true);

        document.documentElement.style.setProperty('--overflow-y-container', 'hidden');
        document.documentElement.style.setProperty('--content-position-loading', 'fixed');

        const params = {
            settings: newTabList
        }

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            try {
                await axios.put(`${SERVER_URL}/settings/Collector`, params, { headers })

                setNewTabList(newTabList);

                setUpdated(true);

                setTimeout(() => {
                    setUpdated(false);
                }, 3000);

                setLoading(false);

                document.documentElement.style.removeProperty('--overflow-y-container');
                document.documentElement.style.removeProperty('--content-position-loading');

            } catch (error: any) {
                setLoading(false);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }


    }

    const resetGeneralsParams = () => {
        getTabList()
    }

    const normalizedGroupSearchValue = groupSearchValue.trim().toLowerCase();
    const normalizedSettingsSearchValue = settingsSearchValue.trim().toLowerCase();
    const filteredTabNameList = normalizedGroupSearchValue
        ? tabNameList.filter((tabName: any) => `${tabName ?? ''}`.toLowerCase().includes(normalizedGroupSearchValue))
        : tabNameList;

    const currTabList = newTabList != '' ? newTabList : tabList;
    const selectedGroupName = filteredTabNameList?.[selected];
    const filteredSettingsList = currTabList
        ?.filter((tab: any) => getTabGroupName(tab) == selectedGroupName)
        ?.filter((tab: any) => {
            if (!normalizedSettingsSearchValue) {
                return true;
            }

            const tabId = `${tab?.id ?? ''}`.toLowerCase();
            const tabDescription = `${tab?.description ?? ''}`.toLowerCase();
            const tabValue = `${tab?.value ?? ''}`.toLowerCase();

            return tabId.includes(normalizedSettingsSearchValue)
                || tabDescription.includes(normalizedSettingsSearchValue)
                || tabValue.includes(normalizedSettingsSearchValue);
        });

    useEffect(() => {
        if (selected < filteredTabNameList.length) {
            return;
        }

        setSelected(0);
    }, [selected, filteredTabNameList.length]);

    return (
        <div className="locator-collector-page">
            {loading && loadingPanel}

            <NotificationGroup
                style={{
                    right: 0,
                    bottom: 50,
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                }}
            >
                <Fade enter={true} exit={true}>
                    {updated && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setUpdated(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.locator.updated', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                </Fade>
            </NotificationGroup>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'stretch', minHeight: '72vh', padding: '10px', height: '100%' }}>
                <div
                    style={{
                        flex: '1.5 1 0px',
                        background: '#F7F7F7',
                        border: '1px solid #d6e8ee',
                        borderRadius: '10px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    <TextBox
                        value={groupSearchValue}
                        onChange={(event: any) => setGroupSearchValue(`${event.target.value ?? ''}`)}
                        placeholder={localizationService.toLanguageString('configuration.locator.locator_server.search_groups_placeholder', 'Search groups')}
                        size="small"
                        style={{ marginBottom: '10px', minHeight: '40px' }}
                    />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                        {filteredTabNameList?.map((tabName: any, index: any) => (
                            <button
                                key={`${tabName}-${index}`}
                                type="button"
                                onClick={() => setSelected(index)}
                                style={{
                                    textAlign: 'left',
                                    border: '1px solid #d6e8ee',
                                    borderRadius: '8px',
                                    padding: '9px 10px',
                                    background: selected === index ? '#198792' : '#ffffff',
                                    color: selected === index ? '#ffffff' : '#198792',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {tabName}
                            </button>
                        ))}
                    </div>

                    <div className="k-listview-item collector-actions-row" style={{ display: 'flex', gap: '10px', margin: '0 auto', marginTop: '10px' }}>
                        <Button
                            className="save-devices-management-button"
                            themeColor={"primary"}
                            svgIcon={arrowRotateCwIcon}
                            onClick={() => resetGeneralsParams()}
                        >
                            {localizationService.toLanguageString('configuration.locator.locator_client.reset', 'NOT_FOUND')}
                        </Button>

                        <Button
                            className="save-devices-management-button"
                            disabled={newTabList?.length == 0 ? true : false}
                            themeColor={"primary"}
                            svgIcon={saveIcon}
                            onClick={() => saveGeneralsParams()}
                        >
                            {localizationService.toLanguageString('configuration.locator.locator_client.save', 'NOT_FOUND')}
                        </Button>
                    </div>
                </div>

                <div
                    style={{
                        flex: '3 1 0',
                        minWidth: 0,
                        minHeight: 0,
                        background: 'white',
                        border: '1px solid #cfe3ea',
                        borderRadius: '10px',
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <TextBox
                        value={settingsSearchValue}
                        onChange={(event: any) => setSettingsSearchValue(`${event.target.value ?? ''}`)}
                        placeholder={localizationService.toLanguageString('configuration.locator.locator_server.search_settings_placeholder', 'Search settings')}
                        size="small"
                        style={{ marginBottom: '10px' }}
                    />

                    <div className="collector-settings-grid" style={{ flex: '1 1 auto', minHeight: 0 }}>
                        {
                            filteredSettingsList
                                .map((tab: any) => {
                                    if (tab.id.includes('DefaultDeviceType')) {
                                        const defaultDeviceTypeOptions = getDefaultDeviceTypeOptionsByTabName(selectedGroupName);
                                        return (
                                            <div className="collector-setting-item" key={`${getTabGroupName(tab)}-${tab.id}`}>
                                                <div className="collector-setting-label">{tab.description}</div>
                                                <DropDownList
                                                    id={tab.id}
                                                    data={defaultDeviceTypeOptions}
                                                    textField="text"
                                                    dataItemKey="value"
                                                    size="small"
                                                    style={{ fontSize: '12px' }}
                                                    defaultValue={defaultDeviceTypeOptions.find((option) => option.value === String(tab.value)) ?? defaultDeviceTypeOptions[0]}
                                                    onChange={(event: any) => handleChangeDropdown(event, tab.id, getTabGroupName(tab))}
                                                />
                                            </div>
                                        )
                                    }

                                    if (tab.id.includes('NetworkServer')) {
                                        return (
                                            <div className="collector-setting-item" key={`${getTabGroupName(tab)}-${tab.id}`}>
                                                <div className="collector-setting-label">{tab.description}</div>
                                                <DropDownList
                                                    id={tab.id}
                                                    data={networkServerOptions}
                                                    textField="text"
                                                    dataItemKey="value"
                                                    defaultValue={networkServerOptions.find((option) => option.value === String(tab.value)) ?? networkServerOptions[0]}
                                                    onChange={(event: any) => handleChangeDropdown(event, tab.id, getTabGroupName(tab))}
                                                />
                                            </div>
                                        )
                                    }

                                    if (!isBooleanOption(tab.id)) {
                                        return (
                                            <div className="collector-setting-item" key={`${getTabGroupName(tab)}-${tab.id}`}>
                                                <div className="collector-setting-label">{tab.description}</div>
                                                <TextBox
                                                    id={tab.id}
                                                    defaultValue={tab.value}
                                                    size="small"
                                                    onChange={(event: any) => handleChangeTextbox(event, tab.id, getTabGroupName(tab))}
                                                    title={tab.description}
                                                    placeholder={tab.description}
                                                />
                                            </div>
                                        )
                                    }

                                    return (
                                        <div className="collector-setting-item collector-setting-item-checkbox" key={`${getTabGroupName(tab)}-${tab.id}`}>
                                            <Checkbox
                                                label={tab.description}
                                                checked={String(tab.value) !== "0"}
                                                onChange={(event: any) => handleChange(event, tab.id, getTabGroupName(tab))}
                                            />
                                        </div>
                                    )
                                })
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}
